import { ICacheSetting, ICommonSettings } from "@kuika/kuika-cl-model";
import { ECacheType } from "@kuika/kuika-cl-model/src/models/common-config";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { IWithSDProps } from "../../../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

/** The fields inside this dialog, for the settings search. See `IConfigFieldProps.searchTerms`. */
export const CACHE_SETTINGS_SEARCH_TERMS: string[] = ["Type", "Connection String"];

export interface IConfigCacheSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingConfig?: ICacheSetting;
}

export interface IConfigCacheSettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingCacheId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => React.ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigCacheSettings extends React.PureComponent<IConfigCacheSettingsProps, IConfigCacheSettingsState> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigCacheSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle"
    };
  }

  componentDidMount() {
    this.loadSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigCacheSettingsProps & IWithSDProps) {
    if (prevProps.editingCacheId !== this.props.editingCacheId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    }
  }

  loadSetting = () => {
    if (!this.props.editingCacheId) return;

    const editingConfig = this.props.commonSettings.cacheSettings?.find((x) => x.id === this.props.editingCacheId);

    if (!editingConfig) return;
    this.setState({ editingConfig }, () => {
      this.formRef.current?.resetFields();
      this.formRef.current?.setFieldsValue(editingConfig);
    });
  };

  existingNameValidator = async (_rule, value) => {
    const cacheSettings = this.props.commonSettings?.cacheSettings;
    if (!cacheSettings) return;

    const isSettingAlreadyExist =
      cacheSettings.some((x) => x.name === value && x.id !== this.props.editingCacheId) || value === "Default";

    if (isSettingAlreadyExist) throw new Error("Cache Name. is already exist.");
  };

  onFinish = (data: any) => {
    this.saveStatus.start();
    let obj: ICacheSetting;
    obj = {
      id: Guid.create().toString(),
      name: data.name,
      type: data.type,
      redisConnectionString: data.redisConnectionString?.trim()
    };
    if (this.props.editingCacheId) {
      obj.id = this.props.editingCacheId;
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "cacheSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "cacheSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteSetting = () => {
    if (!this.props.editingCacheId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingCacheId || "",
      configKey: "cacheSettings",

      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Cache setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Cache setting"
        secondHeader="Cache setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingCacheId ? this.deleteSetting : undefined}
            deleteTestId="cacheSettingDeleteButton"
            submitTestId="cacheSettingCreateButton"
            submitLabel={this.props.editingCacheId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configCacheForm"
          />
        }
      >
        <Form
          id="configCacheForm"
          name="frontendUrlSetting"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody
            description={this.props.getSectionDescription("appsettings_cachesettingspop_up_description")}
          >
            <ConfigField label="Name" required tooltipContentKey="appsettings_cachesettingspop_up_name">
              <Form.Item
                name="name"
                rules={[
                  { required: true, message: "Please enter a Cache Name." },
                  { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "Cache Name is invalid" },
                  { validator: this.existingNameValidator }
                ]}
              >
                <KuikaInput size="md" data-testid="cacheSettingNameInput" autoFocus />
              </Form.Item>
            </ConfigField>
            <ConfigField label="Type" required tooltipContentKey="appsettings_cachesettingspop_up_type">
              <Form.Item
                name="type"
                rules={[{ required: true, message: "Please select a Cache Setting Type." }]}
                initialValue={ECacheType.Redis}
              >
                <KuikaSelectbox size="md" disabled options={[{ value: ECacheType.Redis, label: "Redis" }]} />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Connection String"
              required
              tooltipContentKey="appsettings_cachesettingspop_up_connectionstring"
            >
              <Form.Item
                name="redisConnectionString"
                rules={[{ required: true, message: "Please enter a Redis Connection String." }]}
              >
                <KuikaInput size="md" data-testid="cacheSettingRedisConnectionStringInput" />
              </Form.Item>
            </ConfigField>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
